import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Scale, Calendar, Building, FileText, ArrowUpDown, Users, Filter, X, Search } from 'lucide-react';
import { searchSearchJudgments, type JudgmentPublic } from '@/client';
import { ApiError } from '@/client/core/ApiError';
import useAuth from '@/hooks/useAuth';
import { AppLayout } from "@/components/layout/AppLayout";

interface JudgementCase {
  id: string;
  målnummer: string;
  domstol: string;
  instans: string;
  dom_datum: string;
  parter: string;
  bakgrund: string;
  avtal_typ: string;
  tvist_om_standardvillkor?: boolean;
  filename: string;
  ai_model?: string;
  search_score?: number;
}

type SortField = 'dom_datum';
type SortOrder = 'asc' | 'desc';

const JudgementsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cases, setCases] = useState<JudgementCase[]>([]);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedDomstolar, setSelectedDomstolar] = useState<string[]>([]);
  const [selectedAvtalTyper, setSelectedAvtalTyper] = useState<string[]>([]);
  const [selectedMalnummer, setSelectedMalnummer] = useState<string[]>([]);
  const [availableDomstolar, setAvailableDomstolar] = useState<string[]>([]);
  const [availableAvtalTyper, setAvailableAvtalTyper] = useState<string[]>([]);
  const [availableMalnummer, setAvailableMalnummer] = useState<string[]>([]);
  const [malnummerSearchInput, setMalnummerSearchInput] = useState<string>('');

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  // Fetch judgments from unified search/filter endpoint
  const { data: judgmentsResponse, isLoading: loading, error } = useQuery({
    queryKey: ["judgments", searchQuery, selectedDomstolar, selectedAvtalTyper, selectedMalnummer],
    queryFn: async () => {
      const searchData = {
        limit: 1000,
        ...(searchQuery && searchQuery.trim() && { q: searchQuery.trim() })
      };
      
      // Note: Backend filters (avtal_id, paragraph_id, status) are different from frontend filters
      // For now, we'll still do frontend filtering for court/contract type
      // TODO: Map frontend filters to backend filters if needed
      
      return await searchSearchJudgments(searchData);
    },
    enabled: !!user,
  });

  // Process judgment data when it loads
  useEffect(() => {
    if (judgmentsResponse?.data) {
      const processedCases: JudgementCase[] = [];
      const uniqueDomstolar = new Set<string>();
      const uniqueAvtalTyper = new Set<string>();
      const uniqueMalnummer = new Set<string>();
      
      for (const judgment of judgmentsResponse.data) {
        const classification = judgment.classification;
        
        const processedCase: JudgementCase = {
          id: judgment.id,
          målnummer: classification?.målnummer || 'N/A',
          domstol: classification?.domstol || 'N/A',
          instans: classification?.instans || 'N/A',
          dom_datum: classification?.dom_datum || new Date(judgment.upload_date).toISOString().split('T')[0],
          parter: classification?.parter || 'N/A',
          bakgrund: classification?.bakgrund || (classification ? 'Ingen bakgrund tillgänglig' : 'Klassificering inte tillgänglig'),
          avtal_typ: classification?.avtal_typ || 'Okänd',
          tvist_om_standardvillkor: classification?.tvist_om_standardvillkor,
          filename: judgment.filename,
          ai_model: classification?.ai_provider && classification?.ai_model ? `${classification.ai_provider}/${classification.ai_model}` : undefined,
          search_score: judgment.search_score || undefined
        };
        
        processedCases.push(processedCase);
        
        if (classification?.domstol) uniqueDomstolar.add(classification.domstol);
        if (classification?.avtal_typ) uniqueAvtalTyper.add(classification.avtal_typ);
        if (classification?.målnummer) uniqueMalnummer.add(classification.målnummer);
      }
      
      setCases(processedCases);
      setAvailableDomstolar(Array.from(uniqueDomstolar).sort());
      setAvailableAvtalTyper(Array.from(uniqueAvtalTyper).sort());
      setAvailableMalnummer(Array.from(uniqueMalnummer).sort());
    }
  }, [judgmentsResponse]);

  // Define court hierarchy
  const getCourtPriority = (instans: string): number => {
    const normalized = instans.toLowerCase();
    if (normalized.includes('högsta domstolen') || normalized.includes('hd')) return 1;
    if (normalized.includes('hovrätt')) return 2;
    if (normalized.includes('tingsrätt')) return 3;
    return 4; // Unknown courts go last
  };

  // Group cases by instans with hierarchical and chronological sorting
  const groupCasesByInstans = (cases: JudgementCase[]) => {
    // If we have a search query, the results are already filtered by the backend
    // Only apply frontend filters when no search is active
    const filtered = searchQuery && searchQuery.trim() 
      ? cases  // Use search results as-is
      : cases.filter(case_item => {
          const domstolMatch = selectedDomstolar.length === 0 || selectedDomstolar.includes(case_item.domstol);
          const avtalMatch = selectedAvtalTyper.length === 0 || selectedAvtalTyper.includes(case_item.avtal_typ);
          const malnummerMatch = selectedMalnummer.length === 0 || selectedMalnummer.includes(case_item.målnummer);
          return domstolMatch && avtalMatch && malnummerMatch;
        });

    const grouped = filtered.reduce((acc, case_item) => {
      const instans = case_item.instans;
      if (!acc[instans]) {
        acc[instans] = [];
      }
      acc[instans].push(case_item);
      return acc;
    }, {} as Record<string, JudgementCase[]>);

    // Sort each group - by search score if available, otherwise chronologically
    Object.keys(grouped).forEach(instans => {
      grouped[instans].sort((a, b) => {
        // If both have search scores, sort by score (higher scores first)
        if (a.search_score !== undefined && b.search_score !== undefined) {
          return b.search_score - a.search_score;
        }
        
        // If only one has a search score, prioritize it
        if (a.search_score !== undefined && b.search_score === undefined) return -1;
        if (b.search_score !== undefined && a.search_score === undefined) return 1;
        
        // If neither has search scores, sort chronologically as before
        const comparison = new Date(a.dom_datum).getTime() - new Date(b.dom_datum).getTime();
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    });

    // Sort instans keys by hierarchy
    const sortedInstansKeys = Object.keys(grouped).sort((a, b) => {
      return getCourtPriority(a) - getCourtPriority(b);
    });

    return { grouped, sortedInstansKeys };
  };

  const { grouped: groupedCases, sortedInstansKeys } = groupCasesByInstans(cases);
  const filteredAndSortedCases = Object.values(groupedCases).flat();

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };
  
  const handleDomstolToggle = (domstol: string) => {
    setSelectedDomstolar(prev => 
      prev.includes(domstol) 
        ? prev.filter(d => d !== domstol)
        : [...prev, domstol]
    );
  };
  
  const handleAvtalTypToggle = (avtalTyp: string) => {
    setSelectedAvtalTyper(prev => 
      prev.includes(avtalTyp) 
        ? prev.filter(a => a !== avtalTyp)
        : [...prev, avtalTyp]
    );
  };
  
  const handleMalnummerToggle = (malnummer: string) => {
    setSelectedMalnummer(prev => 
      prev.includes(malnummer) 
        ? prev.filter(m => m !== malnummer)
        : [...prev, malnummer]
    );
  };
  
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedDomstolar([]);
    setSelectedAvtalTyper([]);
    setSelectedMalnummer([]);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
            <p className="text-muted-foreground">Laddar avgöranden...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center space-y-4">
            <p className="text-red-500">Kunde inte ladda avgöranden från servern</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Gå till startsidan
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-7xl space-y-6">
        {/* Filters and Sorting */}
        <Card className="p-4 mb-6 bg-gradient-card shadow-card-legal border border-primary/20">
          <div className="flex items-center justify-between mb-4">
            {(searchQuery || selectedDomstolar.length > 0 || selectedAvtalTyper.length > 0 || selectedMalnummer.length > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Rensa alla filter
              </Button>
            )}
          </div>

          {/* Search Input */}
          <div className="mb-4">
            <form onSubmit={handleSearchSubmit} className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  type="text"
                  placeholder="AI-sök i avgöranden (t.ex. 'skadestånd' eller 'praxis underrättelse oförutsedda markförhållanden 2:7')..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                type="submit"
                variant="default"
                size="sm"
              >
                <Search className="h-4 w-4" />
              </Button>
              {(searchInput || searchQuery) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </form>
            {searchQuery && (
              <p className="text-sm text-muted-foreground mt-2">
                <Search className="inline h-4 w-4 mr-1" />
                Söker efter: "{searchQuery}"
              </p>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-4">
            {/* Domstol Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Domstol:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center space-x-1">
                    <Building className="h-4 w-4" />
                    <span>
                      {selectedDomstolar.length === 0 
                        ? 'Alla domstolar' 
                        : `${selectedDomstolar.length} valda`}
                    </span>
                    <Filter className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Välj domstolar</h4>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {availableDomstolar.map((domstol) => (
                        <div key={domstol} className="flex items-center space-x-2">
                          <Checkbox
                            id={`domstol-${domstol}`}
                            checked={selectedDomstolar.includes(domstol)}
                            onCheckedChange={() => handleDomstolToggle(domstol)}
                          />
                          <label
                            htmlFor={`domstol-${domstol}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {domstol}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Avtal Type Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Avtal:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center space-x-1">
                    <FileText className="h-4 w-4" />
                    <span>
                      {selectedAvtalTyper.length === 0 
                        ? 'Alla avtal' 
                        : `${selectedAvtalTyper.length} valda`}
                    </span>
                    <Filter className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Välj avtalstyper</h4>
                    <div className="space-y-2">
                      {availableAvtalTyper.map((avtalTyp) => (
                        <div key={avtalTyp} className="flex items-center space-x-2">
                          <Checkbox
                            id={`avtal-${avtalTyp}`}
                            checked={selectedAvtalTyper.includes(avtalTyp)}
                            onCheckedChange={() => handleAvtalTypToggle(avtalTyp)}
                          />
                          <label
                            htmlFor={`avtal-${avtalTyp}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {avtalTyp}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Målnummer Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Målnummer:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center space-x-1">
                    <Scale className="h-4 w-4" />
                    <span>
                      {selectedMalnummer.length === 0 
                        ? 'Alla målnummer' 
                        : `${selectedMalnummer.length} valda`}
                    </span>
                    <Filter className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Välj målnummer</h4>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        type="text"
                        placeholder="Sök målnummer..."
                        value={malnummerSearchInput}
                        onChange={(e) => setMalnummerSearchInput(e.target.value)}
                        className="pl-10 text-sm"
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {availableMalnummer
                        .filter(malnummer => 
                          malnummer.toLowerCase().includes(malnummerSearchInput.toLowerCase())
                        )
                        .map((malnummer) => (
                        <div key={malnummer} className="flex items-center space-x-2">
                          <Checkbox
                            id={`malnummer-${malnummer}`}
                            checked={selectedMalnummer.includes(malnummer)}
                            onCheckedChange={() => handleMalnummerToggle(malnummer)}
                          />
                          <label
                            htmlFor={`malnummer-${malnummer}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {malnummer}
                          </label>
                        </div>
                      ))}
                      {availableMalnummer.filter(malnummer => 
                        malnummer.toLowerCase().includes(malnummerSearchInput.toLowerCase())
                      ).length === 0 && malnummerSearchInput && (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          Inga målnummer matchar "{malnummerSearchInput}"
                        </p>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Sorting */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Sortera:</span>
              <Button
                variant="default"
                size="sm"
                onClick={handleSort}
                className="flex items-center space-x-1"
              >
                <Calendar className="h-4 w-4" />
                <span>Datum</span>
                <ArrowUpDown className={`h-3 w-3 transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </Card>

        {/* Cases List - Grouped by Instans */}
        <div className="space-y-8">
          {sortedInstansKeys.map((instans) => (
            <div key={instans} className="space-y-4">
              {/* Instans Header */}
              <div className="flex items-center space-x-3 border-b border-primary/30 pb-2">
                <Scale className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-bold text-primary">{instans}</h2>
                <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                  {groupedCases[instans].length} avgöranden
                </Badge>
              </div>
              
              {/* Cases in this instans */}
              <div className="space-y-4 ml-8">
                {groupedCases[instans].map((case_item, index) => (
                  <Link
                    key={case_item.id}
                    to={`/avgorande/${case_item.id}`}
                    className="block no-underline"
                  >
                    <Card 
                      className="p-6 border border-transparent hover:border-primary/50 hover:shadow-card-legal transition-smooth cursor-pointer animate-fade-in rounded-xl"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <Scale className="h-5 w-5 text-primary" />
                            <div>
                              <h3 className="text-lg font-semibold text-primary">
                                {case_item.målnummer}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {case_item.domstol}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="secondary" className="border border-primary/30 bg-primary/10 text-primary">
                              {case_item.avtal_typ}
                            </Badge>
                            {case_item.search_score !== undefined && (
                              <Badge variant="outline" className="text-xs">
                                Score: {(case_item.search_score * 100).toFixed(1)}%
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Date */}
                        <div className="flex items-center space-x-2 mb-3">
                          <Calendar className="h-4 w-4 text-primary/70" />
                          <span className="text-sm font-medium text-foreground">Datum:</span>
                          <span className="text-sm text-muted-foreground">{formatDate(case_item.dom_datum)}</span>
                        </div>

                        {/* Parties */}
                        <div className="flex items-start space-x-2 mb-3">
                          <Users className="h-4 w-4 text-primary/70 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium text-foreground">Parter: </span>
                            <span className="text-sm text-muted-foreground">{case_item.parter}</span>
                          </div>
                        </div>

                        {/* Background */}
                        <div className="text-sm text-muted-foreground leading-relaxed">
                          {case_item.bakgrund}
                        </div>
                      </div>
                    </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredAndSortedCases.length === 0 && !loading && (
          <div className="text-center py-12">
            <Scale className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {cases.length === 0 ? 'Inga avgöranden funna' : 'Inga avgöranden matchar de valda filtren'}
            </p>
            {(selectedDomstolar.length > 0 || selectedAvtalTyper.length > 0 || selectedMalnummer.length > 0) && (
              <Button
                variant="outline"
                onClick={clearAllFilters}
                className="mt-2"
              >
                Rensa alla filter
              </Button>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default JudgementsList;
